import React, { useState, useEffect } from 'react';
import { UserPlus, Users, Trash2, Edit, Save, X } from 'lucide-react';
import { adminAPI } from '../../services/apiService';
import { clearAdminCache } from '../../utils/authUtils';

const AdminManagement = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        is_active: true
    });

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getAdmins();
            setAdmins(response.admins || []);
        } catch (error) {
            console.error('Failed to fetch admins:', error);
            setError('Failed to load admin list');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const response = await adminAPI.createAdmin(formData);
            
            if (response.status === 'success') {
                setShowAddForm(false);
                setFormData({ email: '', name: '', is_active: true });
                clearAdminCache(); // Clear the admin cache
                fetchAdmins(); // Refresh the list
            }
        } catch (error) {
            console.error('Failed to add admin:', error);
            setError(error.response?.data?.detail || 'Failed to add admin');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (admin) => {
        try {
            setLoading(true);
            const response = await adminAPI.updateAdmin(admin.email, admin);
            
            if (response.status === 'success') {
                setEditingAdmin(null);
                clearAdminCache(); // Clear the admin cache
                fetchAdmins(); // Refresh the list
            }
        } catch (error) {
            console.error('Failed to update admin:', error);
            setError(error.response?.data?.detail || 'Failed to update admin');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (email) => {
        if (!window.confirm('Are you sure you want to remove this admin?')) {
            return;
        }

        try {
            setLoading(true);
            const response = await adminAPI.deleteAdmin(email);
            
            if (response.status === 'success') {
                clearAdminCache(); // Clear the admin cache
                fetchAdmins(); // Refresh the list
            }
        } catch (error) {
            console.error('Failed to delete admin:', error);
            setError(error.response?.data?.detail || 'Failed to delete admin');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading && admins.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center my-16">
                <div className="w-16 h-16 border-4 border-t-transparent border-white rounded-full animate-spin mb-4"></div>
                <p className="text-xl font-avenir text-white">Loading admins...</p>
            </div>
        );
    }

    return (
        <div className="p-8">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-4xl font-avenir font-900 text-center text-white">Admin Management</h1>
                <button
                    onClick={() => setShowAddForm(true)}
                    className="bg-vibrantRed hover:bg-red-800 text-white px-6 py-3 rounded-lg transition flex items-center space-x-2 font-avenir"
                >
                    <UserPlus size={20} />
                    <span>Add Admin</span>
                </button>
            </div>

            {error && (
                <div className="bg-red-900/20 border border-red-700 text-white p-4 rounded-lg mb-6">
                    {error}
                </div>
            )}

            {/* Add Admin Form */}
            {showAddForm && (
                <div className="bg-vibrantRed backdrop-blur-sm rounded-lg p-6 mb-8 border border-gray-700">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-2xl font-avenir text-white">Add New Admin</h2>
                        <button
                            onClick={() => setShowAddForm(false)}
                            className="text-white hover:text-gray-300"
                        >
                            <X size={24} />
                        </button>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-white text-sm font-medium mb-2 font-avenir">Email</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                className="w-full bg-gray-700/50 text-white rounded-md p-2 border border-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                                required
                                placeholder="admin@example.com"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-white text-sm font-medium mb-2 font-avenir">Name</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full bg-gray-700/50 text-white rounded-md p-2 border border-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                                placeholder="Admin Name"
                            />
                        </div>
                        
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={formData.is_active}
                                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                className="mr-2"
                            />
                            <label htmlFor="is_active" className="text-white font-avenir">Active</label>
                        </div>
                        
                        <div className="flex space-x-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-black/80 hover:bg-red-800 text-white px-6 py-2 rounded-lg transition font-avenir disabled:bg-gray-700/50"
                            >
                                {loading ? 'Adding...' : 'Add Admin'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowAddForm(false)}
                                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition font-avenir"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Admins List */}
            <div className="bg-vibrantRed backdrop-blur-sm rounded-lg p-6 border border-gray-700">
                <h2 className="text-2xl font-avenir text-white mb-6 flex items-center">
                    <Users size={24} className="mr-2" />
                    Current Admins ({admins.length})
                </h2>
                
                {admins.length === 0 ? (
                    <div className="text-center text-white py-8">
                        <p className="text-lg font-avenir">No admins found</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {admins.map((admin) => (
                            <div
                                key={admin.email}
                                className="bg-black/30 rounded-lg p-4 border border-gray-600"
                            >
                                {editingAdmin?.email === admin.email ? (
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <h3 className="text-lg font-avenir text-white">Edit Admin</h3>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleEdit(editingAdmin)}
                                                    className="bg-green-600 hover:bg-green-700 text-white p-2 rounded"
                                                >
                                                    <Save size={16} />
                                                </button>
                                                <button
                                                    onClick={() => setEditingAdmin(null)}
                                                    className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </div>
                                        <input
                                            type="text"
                                            value={editingAdmin.name}
                                            onChange={(e) => setEditingAdmin(prev => ({ ...prev, name: e.target.value }))}
                                            className="w-full bg-gray-700/50 text-white rounded-md p-2 border border-gray-600"
                                            placeholder="Admin Name"
                                        />
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={editingAdmin.is_active}
                                                onChange={(e) => setEditingAdmin(prev => ({ ...prev, is_active: e.target.checked }))}
                                                className="mr-2"
                                            />
                                            <label className="text-white font-avenir">Active</label>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-lg font-avenir text-white">{admin.name || 'No name provided'}</h3>
                                            <p className="text-gray-300 font-avenir">{admin.email}</p>
                                            <p className="text-sm text-gray-400 font-avenir">
                                                Status: {admin.is_active ? 'Active' : 'Inactive'} | 
                                                Updated: {formatDate(admin.updated_at)}
                                            </p>
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => setEditingAdmin(admin)}
                                                className="bg-black/90 hover:bg-red-700 text-white p-2 rounded"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(admin.email)}
                                                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminManagement; 